import { useEffect, useRef, useState } from 'react';

const scrollTo = (href: string) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const plans = [
  {
    id: 'essential',
    name: 'Essential',
    price: '$4,800',
    pricePerInstallment: '$1,200',
    tagline: 'Classic silhouettes with refined, understated beauty.',
    features: [
      'Custom design',
      'Curated selection of luxury, high quality fabrics',
      'Measurements taken over video call with you',
      'progress photo updates',
      'Clean, timeless embellishments',
      'Worldwide insured shipping',
      'Preservation packaging',
    ],
    highlight: false,
  },
  {
    id: 'signature',
    name: 'Signature',
    price: '$5,400',
    pricePerInstallment: '$1,350',
    tagline: 'Elevated designs with premium fabrics & detail work.',
    features: [
      'Everything in Essential',
      'Highest premium designer fabric options',
      'Complex designs and draping structures',
      'Detailing options',
      'Concierge Whatsapp with 48hr response',
      'Dedicated design consultant',
      'Rush shipping available',
      'Dedicated team with you every step of the way',
    ],
    highlight: true,
  },
  {
    id: 'couture',
    name: 'Couture',
    price: '$6,700',
    pricePerInstallment: '$1,675',
    tagline: 'Highly complex designs — the pinnacle of artistry.',
    features: [
      'Everything in Signature',
      'Finest couture-grade fabrics',
      'Personalized embellishments',
      'Multi-layer sculpting & structure',
      'Concierage Whatsapp with 24hr response',
      'Video consultations included',
      'Silk-lined preservation box',
      'Priority production queue',
    ],
    highlight: false,
  },
];

const milestones = [
  { label: 'Milestone 1', detail: 'After design approval', percent: '25%' },
  { label: 'Milestone 2', detail: 'Measurements & fabric confirmed', percent: '25%' },
  { label: 'Milestone 3', detail: 'Creation halfway point', percent: '25%' },
  { label: 'Milestone 4', detail: 'Before shipment', percent: '25%' },
];

export default function PricingSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('[data-animate]').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSelect = (planName: string) => {
    setSelectedPlan(planName);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 3500);
    scrollTo('#consultation');
  };

  return (
    <section id="pricing" ref={sectionRef} className="py-28 px-6 lg:px-12 relative" style={{ background: '#1C120A' }}>
      {/* Toast */}
      {toastVisible && (
        <div
          className="fixed top-6 left-1/2 z-50 px-6 py-3.5 rounded-sm text-sm flex items-center gap-3"
          style={{
            background: '#FAF7F3',
            color: '#2C2C2C',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 400,
            transform: 'translateX(-50%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}
        >
          <i className="ri-check-line text-amber-600" />
          <span>Great choice! Let&apos;s book your consultation for the <strong>{selectedPlan}</strong> package.</span>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16" data-animate style={{ opacity: 0, transform: 'translateY(24px)', transition: 'all 0.7s ease' }}>
          <p
            className="inline-block text-xs tracking-widest uppercase px-4 py-1.5 border border-amber-400/30 text-amber-400 mb-6 rounded-sm"
            style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.18em', fontSize: '0.65rem' }}
          >
            Investment
          </p>
          <h2
            className="text-4xl lg:text-5xl mb-5"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: '#FAF7F3', lineHeight: 1.15 }}
          >
            Transparent Pricing,<br />
            <em style={{ fontStyle: 'italic', color: '#C9A96E' }}>Exceptional Value</em>
          </h2>
          <p
            className="text-stone-400 max-w-md mx-auto leading-relaxed"
            style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
          >
            Pricing reflects the complexity of your design and the quality of fabrics chosen. Split payments across 4 milestones — paid as your dress comes to life.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
          {plans.map((plan, index) => (
            <div
              key={plan.id}
              data-animate
              className={`rounded-lg p-8 flex flex-col ${plan.highlight ? 'relative' : ''}`}
              style={{
                background: plan.highlight ? '#C9A96E' : '#2A1D13',
                opacity: 0,
                transform: 'translateY(32px)',
                transition: `all 0.7s ease ${index * 0.15}s`,
                ...(plan.highlight ? { marginTop: '-12px', paddingTop: '40px', paddingBottom: '40px' } : {}),
              }}
            >
              {plan.highlight && (
                <div
                  className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-5 py-1.5 rounded-sm text-xs tracking-widest uppercase"
                  style={{ background: '#FAF7F3', color: '#8B6535', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.14em', fontSize: '0.6rem' }}
                >
                  Most Loved
                </div>
              )}
              <p
                className="text-xs tracking-widest uppercase mb-3"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.16em',
                  color: plan.highlight ? '#FBF2E6' : '#8B7355',
                  fontSize: '0.65rem',
                }}
              >
                {plan.name}
              </p>
              <p
                className="text-4xl mb-1"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 500,
                  color: plan.highlight ? '#FBF0E0' : '#C9A96E',
                  lineHeight: 1,
                }}
              >
                {plan.price}
              </p>
              <p
                className="text-xs mb-1"
                style={{ fontFamily: "'DM Sans', sans-serif", color: plan.highlight ? '#FBF0E0/80' : '#6B5840', fontWeight: 300 }}
              >
                or {plan.pricePerInstallment} &times; 4 installments
              </p>
              <p
                className="text-sm mb-8 mt-3 pb-6 border-b"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: 'italic',
                  color: plan.highlight ? '#FBF2E6' : '#8B7355',
                  borderColor: plan.highlight ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)',
                }}
              >
                {plan.tagline}
              </p>

              <ul className="flex flex-col gap-3 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <i
                      className="ri-check-line mt-0.5 flex-shrink-0"
                      style={{ color: plan.highlight ? '#FBF2E6' : '#C9A96E', fontSize: '0.85rem' }}
                    />
                    <span
                      className="text-sm leading-snug"
                      style={{ fontFamily: "'DM Sans', sans-serif", color: plan.highlight ? '#FBF2E6' : '#A09070', fontWeight: 300 }}
                    >
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelect(plan.name)}
                className="w-full py-3.5 text-xs tracking-widest uppercase cursor-pointer transition-all duration-300 rounded-sm whitespace-nowrap"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  letterSpacing: '0.14em',
                  background: plan.highlight ? '#FAF7F3' : 'transparent',
                  color: plan.highlight ? '#8B6535' : '#C9A96E',
                  border: plan.highlight ? 'none' : '1px solid #C9A96E40',
                }}
              >
                Select {plan.name}
              </button>
            </div>
          ))}
        </div>

        {/* Pay in 4 milestones */}
        <div
          className="mt-16 p-8 rounded-lg"
          data-animate
          style={{ background: '#2A1D13', opacity: 0, transform: 'translateY(24px)', transition: 'all 0.7s ease 0.5s' }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <p
                className="text-xs tracking-widest uppercase text-amber-500/60 mb-2"
                style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.18em', fontSize: '0.65rem' }}
              >
                Payment Structure
              </p>
              <h3
                className="text-2xl"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: '#FAF7F3', fontWeight: 300 }}
              >
                Pay in 4 Milestones
              </h3>
              <p
                className="text-stone-500 text-sm mt-2 max-w-xs"
                style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
              >
                Each payment is tied to a real milestone in your dress&apos;s creation — you only pay as progress is made.
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-1 lg:max-w-2xl">
              {milestones.map((m, i) => (
                <div key={m.label} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs"
                      style={{ background: '#C9A96E20', color: '#C9A96E', fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {i + 1}
                    </div>
                    <span className="text-amber-500 text-sm" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                      {m.percent}
                    </span>
                  </div>
                  <p className="text-xs text-stone-500" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}>{m.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p
          className="text-center text-stone-600 text-xs mt-6"
          style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
        >
          Payments processed securely via Stripe · All packages include complimentary preservation packaging
        </p>
      </div>
    </section>
  );
}
